import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";

import { PageNotFound, StyledGrid } from "components/simple";
import { UserAssessmentCycleStatus } from "components/simple/CycleSummary/types";
import { GetAppropriateAssessmentCycleOverview } from "graphql/admin/assessments";
import { GetUserAssessmentCycleIdsAndNumbers } from "graphql/bhi/cycles.gql";
import { useDispatch } from "hooks";
import { color, spacingDefaults } from "style/constants";
import { AssessmentType, Pillar } from "types";
import AceScores from "./AceScores";
import GroupHeader from "./GroupHeader";
import SingleAssessment from "./SingleAssessment";
import TotalScoreHeader from "./TotalScoreHeader";
import {
  GetAppropriateAssessmentCycleOverviewData,
  GetUserAssessmentCycleIdsAndNumbersData
} from "./types";
import { getDisplayPillarName } from ".";
import BrainHQAssessment from "./BrainHQAssessment";

interface Props {
  userAssessmentCycleId?: string;
}

const UserAssessments: React.FC<Props> = ({ userAssessmentCycleId }) => {
  const dispatch = useDispatch();

  const [userId, setUserId] = useState<string>("");
  const [cycles, setCycles] = useState<GetUserAssessmentCycleIdsAndNumbersData>();
  const [pillars, setPillars] = useState<string[]>([]);
  const [userAssessmentCycleData, setUserAssessmentCycleData] = useState<
    GetAppropriateAssessmentCycleOverviewData
  >();

  const { data: cycleData, loading, error, refetch } = useQuery<
    GetAppropriateAssessmentCycleOverviewData
  >(GetAppropriateAssessmentCycleOverview, {
    variables: {
      id: userAssessmentCycleId,
      refactor: true
    }
  });

  // query to populate cycle dropdown
  const { error: cycleDataError, loading: cycleDataLoading } = useQuery<
    GetUserAssessmentCycleIdsAndNumbersData
  >(GetUserAssessmentCycleIdsAndNumbers, {
    skip: !userId,
    variables: {
      filters: [
        {
          filterColumn: "user_assessment_cycles.user_id",
          filterValue: userId
        }
      ],
      sort: { column: "cycle_number", order: "asc" }
    },
    onCompleted: data => {
      const sortedCycles = data.userAssessmentCycles.data.filter(cycle => {
        return cycle.status != UserAssessmentCycleStatus.NOT_STARTED;
      });
      setCycles({
        userAssessmentCycles: {
          data: sortedCycles
        }
      });
    },
    onError: () => dispatch({ type: "error" })
  });

  useEffect(() => {
    if (cycleData?.userAssessmentCycle) {
      let pillars: string[] = [];
      const updatedCycleData = {
        ...cycleData,
        userAssessmentCycle: {
          ...cycleData.userAssessmentCycle,
          pillarScores: cycleData.userAssessmentCycle.bhiRefactorPillarScores
        }
      };
      setUserAssessmentCycleData(updatedCycleData as GetAppropriateAssessmentCycleOverviewData);
      pillars = cycleData.userAssessmentCycle.bhiRefactorPillars;

      pillars = [...pillars, "About You"]; // report has About You section at bottom
      setPillars(pillars);
      setUserId(cycleData.userAssessmentCycle.user.id);
    }
  }, [cycleData]);

  if (loading || cycleDataLoading) return <></>;
  if (
    error ||
    cycleDataError ||
    !userAssessmentCycleData ||
    !cycles ||
    !userAssessmentCycleData.userAssessmentCycle ||
    !userAssessmentCycleData.userAssessmentCycle.pillarScores
  ) {
    return <PageNotFound />;
  }
  const status = userAssessmentCycleData.userAssessmentCycle.status;
  const indexPillarName = "newIndex";

  function getPillarScore(name: string): number | null {
    if (!userAssessmentCycleData?.userAssessmentCycle.pillarScores) return null;

    const pillar = userAssessmentCycleData.userAssessmentCycle.pillarScores.find(
      (pillar: Pillar) => pillar.pillarName === name
    );

    if (pillar) return pillar.value;
    return null;
  }

  return (
    <>
      <StyledGrid container fillRemainingHeight>
        <StyledGrid
          width="100%"
          margin={`${spacingDefaults.large} auto`}
          backgroundColor={color.WHITE}
          border={`solid 1px ${color.MUTEDGRAY}`}
          borderRadius="8px"
        >
          <TotalScoreHeader
            score={getPillarScore(indexPillarName)}
            status={status}
            readyToFinalize={userAssessmentCycleData.userAssessmentCycle.readyToFinalize}
            generalNote={userAssessmentCycleData.userAssessmentCycle.internalNotes?.length}
          />
          {pillars.map(p => {
            const userAssessmentsForPillar = userAssessmentCycleData.userAssessmentCycle.userAssessments.filter(
              ua => ua.assessment.pillarAssessments.some(pa => pa.pillarName === p)
            );
            userAssessmentsForPillar.sort(
              (a: AssessmentType, b: AssessmentType) =>
                a.assessment.assessmentGrouping.assessmentPart.order -
                  b.assessment.assessmentGrouping.assessmentPart.order ||
                a.assessment.assessmentGrouping.order - b.assessment.assessmentGrouping.order ||
                a.assessment.order - b.assessment.order
            );
            return (
              <>
                <GroupHeader
                  key={p}
                  name={getDisplayPillarName(p)}
                  pillarScore={getPillarScore(p)}
                />
                {userAssessmentsForPillar.map(userAssessment => {
                  const isAutomatedScored = userAssessment.assessment.automatedScoring;
                  const assessmentName = userAssessment.assessment.displayName;
                  const scoreRanges = userAssessment.scoreRanges;

                  // strategy has a special scoring system, the assessment score is named "Aggregate"
                  // also includes the individual question raw scores (OBPI-1B, OBPI-1D, and OBPI-1F)
                  const isStrategy = assessmentName === "Strategy";
                  const scores = isStrategy
                    ? userAssessment.scores.filter(
                        (s: { scoreName: string }) => s.scoreName === "Aggregate"
                      )
                    : userAssessment.scores;
                  return (
                    <SingleAssessment
                      id={userAssessment.id}
                      key={userAssessment.id}
                      name={assessmentName}
                      notes={
                        userAssessment.internalNotes?.length
                          ? userAssessment.internalNotes.length
                          : undefined
                      }
                      scores={scores}
                      scoreRanges={scoreRanges}
                      automatedScored={isAutomatedScored}
                      status={status}
                      isClaimedByCurrentUser={
                        userAssessmentCycleData?.userAssessmentCycle.isClaimedByCurrentUser || false
                      }
                      nextAssesmentIds={{}}
                      validityCode={userAssessment.validityCode}
                    />
                  );
                })}
              </>
            );
          })}
        </StyledGrid>
        <StyledGrid item marginBottom={spacingDefaults.large} xs={12}>
          {/* <AceScores aceScores={userAssessmentCycleData.userAssessmentCycle.aceScores} /> */}
          <BrainHQAssessment
            score={
              userAssessmentCycleData.userAssessmentCycle.userBrainHqAssessment?.summaryStatistics
            }
          />
        </StyledGrid>
      </StyledGrid>
    </>
  );
};

export default UserAssessments;
